import { z } from 'zod';
import { decimalNoNegativo, decimalPositivo, decimalString, paginacion, uuid } from './comun.ts';
import type { CorreoAutomaticoDTO } from './correo.ts';

export const MOTIVOS_AJUSTE = ['Rotura', 'Merma', 'Daño', 'Pérdida', 'Diferencia de conteo', 'Diferencia de traslado', 'Inventario inicial', 'Uso interno', 'Otro'] as const;
export type MotivoAjuste = (typeof MOTIVOS_AJUSTE)[number];

export const entregaInput = z.object({
  almacen_id: uuid,
  lineas: z.array(z.object({ documento_linea_id: uuid, cantidad: decimalPositivo })).min(1, 'Indica qué entregar').max(200),
  recibe_nombre: z.string().trim().max(120).nullable().optional(),
  recibe_documento: z.string().trim().max(40).nullable().optional(),
  observaciones: z.string().trim().max(500).optional(),
  /** Autorizaciones aprobadas: la de «Entrega con la retención por recibir» cuando la pide el servidor. */
  autorizaciones: z.array(uuid).max(10).optional(),
});
export type EntregaInput = z.infer<typeof entregaInput>;

/**
 * «Dejar pendiente» (Fase 1 de retiros, respuestas del 2026-10-29): sobre una entrega del MISMO día, lo que salió y el
 * cliente al final no se lleva vuelve al almacén y queda apartado otra vez. Motivos sugeridos en la pantalla; «Otro»
 * se escribe. El servidor pide un motivo de 3 a 300 caracteres.
 */
export const MOTIVOS_DEJAR_PENDIENTE = ['No cupo en el vehículo', 'Retira después'] as const;
export const dejarPendienteInput = z
  .object({
    motivo: z.string().trim().min(3, 'Escribe el motivo (por ejemplo, «No cupo en el vehículo»)').max(300),
    lineas: z.array(z.object({ documento_linea_id: uuid, cantidad: decimalPositivo })).min(1, 'Indica qué queda pendiente').max(200),
  })
  .strict();
export type DejarPendienteInput = z.infer<typeof dejarPendienteInput>;

export const ajusteInput = z.object({
  almacen_id: uuid,
  motivo: z.enum(MOTIVOS_AJUSTE),
  justificacion: z.string().trim().min(5, 'Justifica el ajuste (mínimo 5 caracteres)').max(1000),
  lineas: z.array(z.object({ producto_id: uuid, cantidad: decimalString.refine((s) => /[1-9]/.test(s), 'La cantidad no puede ser cero') })).min(1).max(200),
});
export type AjusteInput = z.infer<typeof ajusteInput>;

export const resolverAjusteInput = z.object({ comentario: z.string().trim().max(500).optional() });

/**
 * Vista previa de los libros de inventario de un ajuste que todavía no se guarda (pedido del usuario del 2026-10-30): el
 * motivo y los renglones, sin almacén ni justificación (la vista sale mientras se escriben).
 */
export const vistaLibrosAjusteInput = z.object({
  motivo: z.enum(MOTIVOS_AJUSTE),
  lineas: z.array(z.object({ producto_id: uuid, cantidad: decimalString.refine((s) => /[1-9]/.test(s), 'La cantidad no puede ser cero') })).min(1).max(200),
});
export type VistaLibrosAjusteInput = z.infer<typeof vistaLibrosAjusteInput>;

/** Cómo entra cada renglón de un ajuste a los libros de inventario real y fiscal (la vista previa). */
export interface VistaLibrosAjusteDTO {
  /** true: se aplica al guardar (la dueña) o al aprobar; false: queda pendiente y entra el mes en que se apruebe. */
  aplica_ahora: boolean;
  /** Mes (AAAAMM) del libro si se aplica ahora; null si queda pendiente. */
  mes: string | null;
  /** BCV con que el libro valora el inventario inicial si se aplica ahora. */
  tasa_bcv: string;
  lineas: {
    producto_id: string;
    codigo: string;
    nombre: string;
    unidad: string;
    cantidad: string;
    columna: 'entradas' | 'salidas' | 'retiros' | 'autoconsumos';
    concepto: string;
    /** Entra también al libro de inventario fiscal (solo el inventario inicial). */
    fiscal: boolean;
    /** Costo en $ del renglón (el del producto al registrar; al aprobar, el que guardó el ajuste). */
    costo_usd: string;
    /** Solo el inventario inicial tiene valor propio: unidades × costo × BCV. Lo demás va al costo promedio del mes. */
    valor_bs: string | null;
  }[];
}

export const despieceInput = z.object({
  almacen_id: uuid,
  producto_id: uuid,
  cajas: decimalPositivo,
  piezas: decimalNoNegativo,
  justificacion: z.string().trim().min(5).max(1000),
});
export type DespieceInput = z.infer<typeof despieceInput>;

export const conteoCrearInput = z.object({ almacen_id: uuid, observaciones: z.string().trim().max(500).optional() });
export const conteoFisicoInput = z.object({ lineas: z.array(z.object({ producto_id: uuid, fisico: decimalNoNegativo })).min(1).max(2000) });
export const conteoConfirmarInput = z.object({ forzar: z.boolean().default(false) });

export const inventarioFiltros = paginacion.extend({
  q: z.string().trim().max(120).optional(),
  almacen_id: uuid.optional(),
  /** La dueña o un usuario global: otra sucursal o «todas» (solo lectura). Los demás ven siempre la suya. */
  sucursal: z.union([uuid, z.literal('todas')]).optional(),
  /** Con existencia o con algo apartado (desde el 2026-11-01 también lo apartado sin existencia). */
  solo_con_stock: z.enum(['true', 'false']).optional().transform((v) => v === 'true'),
  /** Solo lo que tiene algo apartado (vendido que falta por entregar). */
  solo_apartado: z.enum(['true', 'false']).optional().transform((v) => v === 'true'),
});

/** A quién está apartado un producto en un almacén. */
export const apartadoQuery = z.object({ producto_id: uuid, almacen_id: uuid });

/** Pendientes y entregas: búsqueda y rango de fechas (día de emisión / de entrega, zona Caracas). Pedido del cliente 03-10-2026. */
export const pendientesFiltros = paginacion.extend({ q: z.string().trim().max(80).optional(), desde: z.iso.date().optional(), hasta: z.iso.date().optional() });
export const ajustesFiltros = paginacion.extend({ estado: z.enum(['pendiente', 'aprobado', 'rechazado']).optional(), tipo: z.enum(['ajuste', 'despiece', 'conteo', 'traslado']).optional() });

// ─── DTOs ───────────────────────────────────────────────────────────────────
export interface StockFilaDTO {
  producto_id: string;
  codigo: string;
  nombre: string;
  unidad: string;
  fragil: boolean;
  almacen_id: string;
  almacen_nombre: string;
  /** `tienda` o `deposito`. */
  almacen_tipo: string;
  sucursal_id: string;
  sucursal_nombre: string;
  existencia: string;
  /** Lo apartado: vendido (factura o nota) que falta por entregar, o apartado aquí por una orden de retiro. */
  pendiente: string;
  disponible: string;
  minimo: string | null;
  costo_real: string | null;
  /** Totales del producto en todos los almacenes del listado (con los mismos filtros), aunque estén en otra página. */
  total: { existencia: string; pendiente: string; disponible: string; almacenes: number };
  /** Lo mismo, solo en los almacenes de la tienda (sucursal) de esta fila. */
  total_tienda: { existencia: string; pendiente: string; disponible: string; almacenes: number };
}

/** El listado de stock con sus almacenes (para el filtro) y la sucursal que muestra. */
export interface StockListaDTO {
  datos: StockFilaDTO[];
  total: number;
  pagina: number;
  limite: number;
  almacenes: { id: string; nombre: string; tipo: string; sucursal_id: string; sucursal_nombre: string }[];
  /** null = todas las sucursales. */
  sucursal_id: string | null;
}

/** A quién está apartado un producto en un almacén: cada renglón de venta por entregar y cada orden de retiro. */
export interface ApartadoDTO {
  producto: { id: string; codigo: string; nombre: string; unidad: string };
  almacen: { id: string; nombre: string; sucursal_nombre: string };
  /** Lo apartado que guarda el inventario (la columna del listado). */
  apartado: string;
  /** La suma de los renglones de abajo: igual a `apartado`. */
  suma: string;
  renglones: {
    /** `venta`: renglón por entregar de una factura o nota de esta sucursal. `orden_retiro`: apartado aquí para un cliente que retira en esta tienda una venta de otra. */
    origen: 'venta' | 'orden_retiro';
    documento_id: string;
    tipo: string;
    numero_formateado: string;
    cliente_razon_social: string;
    emitido_en: string;
    sucursal_venta_nombre: string;
    cantidad: string;
    /** La orden de retiro: la que trajo lo apartado aquí, o (en una venta) a la que pasó otra parte del renglón. */
    orden: { numero_formateado: string; sucursal_retiro_nombre: string; vence_en: string; cantidad: string } | null;
  }[];
}

export interface MovimientoDTO {
  id: string;
  ocurrido_en: string;
  almacen_nombre: string;
  tipo: string;
  cantidad: string;
  existencia_antes: string;
  existencia_despues: string;
  pendiente_antes: string;
  pendiente_despues: string;
  referencia_tipo: string | null;
  referencia_id: string | null;
  referencia_numero: string | null;
  usuario_nombre: string | null;
  motivo: string | null;
}

export interface PendienteEntregaDTO {
  documento_id: string;
  tipo: string;
  numero_formateado: string;
  cliente_razon_social: string;
  emitido_en: string;
  dias: number;
  /** Días hábiles (lunes a viernes) desde la venta; `vencido` = pasó el plazo de retiro de Configuración. */
  dias_habiles: number;
  vencido: boolean;
  estado_entrega: string;
  unidades_pendientes: string;
  /**
   * Retenciones de la factura que siguen «Por recibir» (cliente que paga base + 25 %): mientras haya alguna, la entrega
   * pide la autorización de la dueña o de un administrador (respuesta de la dueña del 26-10-2026).
   */
  retenciones_por_recibir: { tipo: 'iva' | 'municipal'; monto: string }[];
  /**
   * almacen_id / almacen_nombre: de dónde se retira el renglón (donde se reservó). null en piezas de vidrio.
   * `en_orden_retiro`: lo de este renglón apartado en otra sucursal con la orden de retiro abierta (Fase 4): aquí se
   * entrega a lo sumo `pendiente − en_orden_retiro`.
   */
  lineas: { documento_linea_id: string; producto_id: string; producto_codigo: string; producto_nombre: string; almacen_id: string | null; almacen_nombre: string | null; sin_inventario: boolean; unidades: string; entregado: string; pendiente: string; en_orden_retiro: string }[];
  /** Último correo «Estado de su retiro» de esta venta (automático o a mano): si salió y cuándo. null = nunca. */
  correo_retiro: { estado: 'enviado' | 'fallido'; enviado_en: string } | null;
  /** Tuvo algo «dejado pendiente» (el cliente no se llevó todo lo de una entrega). */
  dejado_pendiente: boolean;
  /** La orden de retiro abierta de esta venta (retira en otra sucursal), si tiene. */
  orden_retiro: { id: string; numero_formateado: string; estado: string; sucursal_retiro_nombre: string; almacen_retiro_nombre: string; vence_en: string } | null;
}

/** Una entrega hecha en otra sucursal con una orden de retiro (Fase 4): de dónde salió y su guía de traslado. */
export interface RetiroEnOtraSucursalDTO {
  orden_id: string;
  orden_numero: string;
  sucursal_venta_nombre: string;
  sucursal_retiro_nombre: string;
  almacen_retiro_nombre: string;
  traslado_id: string;
  traslado_numero: string;
}

export interface EntregaDTO {
  id: string;
  numero_formateado: string;
  documento_id: string;
  documento_numero: string;
  documento_tipo: string;
  cliente_razon_social: string;
  cliente_rif: string;
  almacen_nombre: string;
  usuario_nombre: string;
  entregado_en: string;
  recibe_nombre: string | null;
  recibe_documento: string | null;
  observaciones: string | null;
  sucursal_nombre: string;
  /** `dejado_pendiente`: lo de este renglón que después se dejó pendiente («Dejar pendiente»). La hoja impresa no cambia. */
  lineas: { documento_linea_id: string; producto_codigo: string; producto_nombre: string; unidad: string; cantidad: string; pendiente_despues: string; unidades_doc: string; dejado_pendiente: string }[];
  /** Renglones de la venta que no van en esta entrega y siguen por entregar. */
  otros_pendientes: { producto_codigo: string; producto_nombre: string; unidad: string; vendido: string; pendiente: string }[];
  documento_estado_entrega: string;
  /** Constancias de pendiente por retirar hechas sobre esta entrega. */
  reingresos: { id: string; numero_formateado: string; registrado_en: string; motivo: string; usuario_nombre: string }[];
  /** «Dejar pendiente» se puede usar: la entrega es de hoy (hora de Caracas) y queda algo que no se dejó pendiente. */
  dejar_pendiente_disponible: boolean;
  /** La entrega se hizo en otra sucursal con una orden de retiro (null en las de siempre). */
  retiro_otra_sucursal: RetiroEnOtraSucursalDTO | null;
  /** Solo en la respuesta de registrar: qué pasó con el correo automático «Estado de su retiro». */
  correo_retiro?: CorreoAutomaticoDTO;
}

/** Constancia de pendiente por retirar (lo que dejó pendiente un «Dejar pendiente»). */
export interface ReingresoDTO {
  id: string;
  numero_formateado: string;
  entrega_id: string;
  entrega_numero: string;
  documento_id: string;
  documento_numero: string;
  documento_tipo: string;
  cliente_razon_social: string;
  cliente_rif: string;
  /** El almacén que despachó: allí volvió la mercancía. */
  almacen_nombre: string;
  usuario_nombre: string;
  registrado_en: string;
  motivo: string;
  sucursal_nombre: string;
  /** Lo que se dejó pendiente y lo que queda por retirar de cada renglón. */
  lineas: { producto_codigo: string; producto_nombre: string; unidad: string; cantidad: string; pendiente_despues: string; unidades_doc: string }[];
  /** Lo demás de la venta que sigue por retirar. */
  otros_pendientes: { producto_codigo: string; producto_nombre: string; unidad: string; vendido: string; pendiente: string }[];
  documento_estado_entrega: string;
  /** Solo en la respuesta de registrar: qué pasó con el correo automático «Estado de su retiro». */
  correo_retiro?: CorreoAutomaticoDTO;
}

/**
 * Estado del retiro de una venta (correo «Estado de su retiro» y su texto para WhatsApp). Sin precios ni tasas.
 * Comprado = lo vendido menos lo devuelto; retirado = lo entregado menos lo devuelto; pendiente = lo que falta.
 */
export interface EstadoRetiroMaterialDTO {
  documento_id: string;
  documento_tipo: 'FAC' | 'NOTA';
  documento_numero: string;
  emitido_en: string;
  documento_estado: string;
  estado_entrega: string;
  cliente_id: string;
  cliente_razon_social: string;
  cliente_rif: string;
  sucursal_id: string;
  sucursal_nombre: string;
  sucursal_direccion: string;
  /** Fijo y WhatsApp de la tienda. */
  telefonos: string[];
  /** Dónde se retira lo pendiente: el almacén donde está apartado, con su dirección si la tiene. */
  retiro_en: { nombre: string; direccion: string | null }[];
  lineas: { producto_codigo: string; producto_nombre: string; unidad: string; comprado: string; retirado: string; pendiente: string }[];
  /** Plazo de Configuración (`retiro_material_dias_habiles`; 0 = sin plazo) y hasta qué día (AAAA-MM-DD). */
  plazo_dias_habiles: number;
  vence_en: string | null;
  dias_habiles: number;
  /** Ya retiró todo. */
  completo: boolean;
}

export interface AjusteDTO {
  id: string;
  numero_formateado: string;
  tipo: string;
  motivo: string;
  justificacion: string;
  estado: string;
  almacen_nombre: string;
  creado_por_nombre: string;
  creado_en: string;
  resuelto_por_nombre: string | null;
  resuelto_en: string | null;
  comentario_resolucion: string | null;
  /** Lo propuso (o confirmó el conteo) quien lo está viendo: no lo aprueba, salvo la dueña (decisión del 2026-10-27). */
  es_propio: boolean;
  /** Valor a costo: null para quien no tiene el permiso de costos. */
  valor_total: string | null;
  lineas: { producto_id: string; producto_codigo: string; producto_nombre: string; cantidad: string; costo_unitario: string | null; existencia_antes: string | null; existencia_despues: string | null }[];
}

export interface ConteoDTO {
  id: string;
  numero_formateado: string;
  almacen_id: string;
  almacen_nombre: string;
  estado: string;
  creado_por_nombre: string;
  creado_en: string;
  confirmado_en: string | null;
  ajuste_id: string | null;
  ajuste_numero: string | null;
  observaciones: string | null;
  lineas: { producto_id: string; producto_codigo: string; producto_nombre: string; sistema: string; fisico: string | null; diferencia: string | null }[];
  movimientos_desde_snapshot: number;
}

// ─── Piezas de vidrio y productos con detalles (bloque 6, 2026-10-09; dueña, 2026-10-15) ─
/** Medida en milímetros, como la escribe la dueña («1400x3660»): hasta un decimal. */
const medidaMm = decimalPositivo.refine((v) => /^\d+(\.\d)?$/.test(v), 'Medida en milímetros (hasta un decimal)').refine((v) => Number(v) >= 1 && Number(v) <= 10000, 'Entre 1 y 10.000 mm');
/** Unidades enteras: las piezas y los productos con detalles se venden enteros. */
const unidades = z.string().trim().regex(/^\d{1,4}$/, 'Cantidad entera').refine((v) => Number(v) >= 1, 'Al menos 1');
/** Foto de la prueba, ya reducida en la pantalla: PNG o JPEG en data URL (la API revisa que lo sea de verdad). */
export const fotoReporte = z.string().regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, 'La foto debe ser una imagen PNG o JPEG').max(1_000_000, 'La foto pesa demasiado');
/** Fotos por reporte (respuesta de la dueña del 26-10-2026: «que se puedan subir varias»). */
export const FOTOS_REPORTE_MAX = 5;
/** Varias fotos de la prueba; `foto` (una sola) se sigue aceptando de las pantallas viejas. Entre las dos, a lo sumo 5. */
const fotosReporte = z.array(fotoReporte).max(FOTOS_REPORTE_MAX, `A lo sumo ${FOTOS_REPORTE_MAX} fotos por reporte`);

/** Pieza buena que salió de la lámina: medida en milímetros y cuántas de esa medida. El precio lo pone la dueña. */
const piezaInput = z.object({
  alto_mm: medidaMm,
  ancho_mm: medidaMm,
  cantidad: unidades.default('1'),
  descripcion: z.string().trim().max(120).optional(),
});
/** Reporte de rotura de vidrio. */
export const despieceVidrioInput = z.object({
  almacen_id: uuid,
  /** La lámina que se partió (con su medida en la ficha). */
  producto_id: uuid,
  /** Láminas partidas (1 · 0,5 …). */
  cantidad: decimalPositivo.default('1'),
  motivo: z.string().trim().min(3, 'Indica qué pasó con la lámina').max(300),
  foto: fotoReporte.optional(),
  fotos: fotosReporte.optional(),
  /** Lo que salió bueno; puede ir vacío (se perdió toda). */
  piezas: z.array(piezaInput).max(40).default([]),
});
export type DespieceVidrioInput = z.infer<typeof despieceVidrioInput>;
export type DespieceVidrioEntrada = z.input<typeof despieceVidrioInput>;

/** Reporte de producto con detalle: cuál, cuántos y qué tiene («tapa rota», «vencido»), con la foto. */
export const productoDetalleInput = z.object({
  almacen_id: uuid,
  producto_id: uuid,
  cantidad: unidades,
  detalle: z.string().trim().min(3, 'Indica el detalle (tapa rota, vencido…)').max(120),
  foto: fotoReporte.optional(),
  fotos: fotosReporte.optional(),
});
export type ProductoDetalleInput = z.infer<typeof productoDetalleInput>;

/**
 * Revisión de la dueña: arma cada pieza para la venta con su nombre, la cantidad (a lo sumo la reportada) y el precio
 * en $. La que deja sin precio, o con cantidad 0, queda como merma; lo que aprueba de menos, también. En un producto
 * con detalle pone el precio especial (obligatorio: para descartarlo, se rechaza).
 */
export const despieceVidrioAprobarInput = z.object({
  piezas: z
    .array(
      z.object({
        pieza_id: uuid,
        nombre: z.string().trim().min(3, 'El nombre de la pieza es muy corto').max(120).optional(),
        cantidad: z.string().trim().regex(/^\d{1,4}$/, 'Cantidad entera').optional(),
        precio_divisa: decimalNoNegativo.nullable(),
      }),
    )
    .max(40)
    .default([]),
  comentario: z.string().trim().max(300).optional(),
});
export type DespieceVidrioAprobarInput = z.infer<typeof despieceVidrioAprobarInput>;
export const despieceVidrioRechazarInput = z.object({ comentario: z.string().trim().min(3, 'Indica el motivo').max(300) });
export const CLASES_PIEZA = ['vidrio', 'detalle'] as const;
export type ClasePieza = (typeof CLASES_PIEZA)[number];
export const despiecesVidrioFiltros = z.object({ estado: z.enum(['por_aprobar', 'aprobado', 'rechazado']).optional(), clase: z.enum(CLASES_PIEZA).optional() });
export const ESTADOS_PIEZA = ['por_aprobar', 'disponible', 'agotada', 'merma', 'rechazada'] as const;
export type EstadoPieza = (typeof ESTADOS_PIEZA)[number];
export const piezasFiltros = z.object({ q: z.string().trim().max(80).optional(), estado: z.enum(ESTADOS_PIEZA).optional(), clase: z.enum(CLASES_PIEZA).optional(), producto_id: uuid.optional() });
/** Cambiar el precio de lo que queda de una pieza o de un producto con detalle (quien fija precios). */
export const piezaEditarInput = z.object({ precio_divisa: decimalPositivo });
/**
 * Salida sin venta, que decide la dueña en cada caso (respuesta del 2026-10-15), siempre con el motivo: darlo de baja
 * (pérdida), usarlo en la tienda («pintar la oficina») o, en un producto con detalle, volverlo a la existencia normal.
 */
export const TIPOS_SALIDA_PIEZA = ['baja', 'uso_interno', 'reintegro'] as const;
export type TipoSalidaPieza = (typeof TIPOS_SALIDA_PIEZA)[number];
export const TIPO_SALIDA_PIEZA_ETIQUETA: Record<TipoSalidaPieza, string> = { baja: 'Dar de baja (pérdida)', uso_interno: 'Uso en la tienda', reintegro: 'Volver a la existencia normal' };
export const piezaSalidaInput = z.object({ tipo: z.enum(TIPOS_SALIDA_PIEZA), cantidad: unidades, motivo: z.string().trim().min(3, 'Escribe el motivo').max(200) });
export type PiezaSalidaInput = z.infer<typeof piezaSalidaInput>;

export interface PiezaSalidaDTO {
  id: string;
  tipo: TipoSalidaPieza;
  cantidad: string;
  motivo: string;
  usuario_nombre: string;
  creado_en: string;
}
/**
 * Una pieza de vidrio (todas las de una medida que salieron de un reporte) o un lote de producto con detalle. En la
 * lista del punto de venta está mientras tiene `disponible`; agotada, queda en el historial.
 */
export interface PiezaDTO {
  id: string;
  clase: ClasePieza;
  codigo: string;
  /** «Vidrio claro 3mm 1400x3660»; en un producto con detalle, el nombre del producto. */
  nombre: string;
  producto_id: string;
  producto_codigo: string;
  producto_nombre: string;
  /** Presentación base del producto (la usa el renglón de venta). */
  presentacion_id: string | null;
  unidad: string;
  almacen_id: string;
  almacen_nombre: string;
  /** Medida en milímetros (piezas de vidrio). */
  alto_mm: string | null;
  ancho_mm: string | null;
  /** Nota de la pieza, o el detalle del producto («tapa rota»). */
  descripcion: string | null;
  /** Lo reportado, lo aprobado para la venta y lo que queda. */
  declarada: string;
  cantidad: string;
  disponible: string;
  /** Unidades vendidas (sin las devueltas). */
  vendidas: string;
  /** $ reales; null mientras no la apruebe la dueña. */
  precio_divisa: string | null;
  /** Precio normal del producto (lista por defecto): en un producto con detalle, para comparar con el especial. */
  precio_normal: string | null;
  estado: EstadoPieza;
  despiece_id: string;
  despiece_numero: string;
  salidas: PiezaSalidaDTO[];
  creado_en: string;
}
export interface DespieceVidrioDTO {
  id: string;
  clase: ClasePieza;
  numero_formateado: string;
  sucursal_nombre: string;
  almacen_nombre: string;
  producto_id: string;
  producto_codigo: string;
  producto_nombre: string;
  /** Láminas partidas, o unidades con detalle. */
  cantidad: string;
  /** Qué pasó con la lámina, o el detalle del producto. */
  motivo: string;
  /** Medida de la lámina al reportar (piezas de vidrio). */
  lamina: { largo_mm: number; ancho_mm: number; espesor_mm: string | null } | null;
  tiene_foto: boolean;
  /** Cuántas fotos tiene (hasta 5). */
  fotos: number;
  estado: 'por_aprobar' | 'aprobado' | 'rechazado';
  creado_por_nombre: string;
  creado_en: string;
  resuelto_por_nombre: string | null;
  resuelto_en: string | null;
  comentario: string | null;
  piezas: PiezaDTO[];
}

// ─── Descuentos programados (bloque 6, 2026-10-09) ──────────────────────────
export const ALCANCES_DESCUENTO = ['producto', 'categoria', 'marca'] as const;
export type AlcanceDescuento = (typeof ALCANCES_DESCUENTO)[number];
export const descuentoProgramadoInput = z
  .object({
    nombre: z.string().trim().min(3, 'Ponle un nombre (mínimo 3 caracteres)').max(80),
    alcance: z.enum(ALCANCES_DESCUENTO),
    producto_id: uuid.optional(),
    categoria_id: uuid.optional(),
    marca: z.string().trim().min(1).max(80).optional(),
    porcentaje: decimalPositivo.refine((v) => Number(v) <= 100, 'El porcentaje no puede superar 100'),
    desde: z.iso.date(),
    hasta: z.iso.date(),
    /** Solo una tienda; sin indicar = todas. */
    sucursal_id: uuid.optional(),
  })
  .refine((d) => d.hasta >= d.desde, { message: 'La fecha final no puede ser anterior a la inicial', path: ['hasta'] })
  .refine((d) => (d.alcance === 'producto' ? !!d.producto_id : d.alcance === 'categoria' ? !!d.categoria_id : !!d.marca), { message: 'Indica a qué se aplica el descuento', path: ['alcance'] });
export type DescuentoProgramadoInput = z.infer<typeof descuentoProgramadoInput>;
export type DescuentoProgramadoEntrada = z.input<typeof descuentoProgramadoInput>;
export const descuentosFiltros = z.object({ vigentes: z.enum(['true', 'false']).optional() });

export interface DescuentoProgramadoDTO {
  id: string;
  nombre: string;
  alcance: AlcanceDescuento;
  /** A qué se aplica, en texto: el producto, la categoría o la marca. */
  objetivo: string;
  producto_id: string | null;
  categoria_id: string | null;
  marca: string | null;
  porcentaje: string;
  desde: string;
  hasta: string;
  sucursal_id: string | null;
  sucursal_nombre: string | null;
  activo: boolean;
  /** vigente hoy · programado (aún no empieza) · vencido · inactivo. */
  estado: 'vigente' | 'programado' | 'vencido' | 'inactivo';
  creado_por_nombre: string;
  creado_en: string;
}
